/**
 * NuovoBonifico — Form creazione bonifico bancario.
 *
 * Carica la lista dei conti correnti per il conto sorgente.
 * Autocomplete destinatario da anagrafica fornitori.
 * Alla submit invia a POST /api/v1/banca/bonifici e redirige
 * all'url_autorizzazione restituito dal provider bancario.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@floraltech/ui';

// --- Tipi -------------------------------------------------------------------

interface Conto {
  id: string;
  nome: string;
  iban: string;
  valuta: string;
}

interface Soggetto {
  id: string;
  ragione_sociale: string | null;
  nome: string | null;
  cognome: string | null;
  iban: string | null;
}

interface FormState {
  conto_id: string;
  iban_destinatario: string;
  nome_destinatario: string;
  importo: string;
  causale: string;
  fornitore_id: string;
}

// --- Helpers ----------------------------------------------------------------

const authGet = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
  'Content-Type': 'application/json',
});

/** Restituisce il nome display di un soggetto. */
function nomeDisplay(s: Soggetto): string {
  return s.ragione_sociale || [s.nome, s.cognome].filter(Boolean).join(' ') || '—';
}

/** Validazione IBAN italiano: inizia con IT, 27 caratteri totali. */
function isIbanValido(iban: string): boolean {
  const clean = iban.replace(/\s/g, '').toUpperCase();
  return clean.startsWith('IT') && clean.length === 27;
}

// --- Componente -------------------------------------------------------------

/**
 * Form per disporre un nuovo bonifico bancario.
 * Dopo la submit redirige all'URL di autorizzazione del provider.
 */
export function NuovoBonifico() {
  const navigate = useNavigate();

  const [conti, setConti] = useState<Conto[]>([]);
  const [soggetti, setSoggetti] = useState<Soggetto[]>([]);
  const [filtroSoggetti, setFiltroSoggetti] = useState<Soggetto[]>([]);
  const [showSuggerimenti, setShowSuggerimenti] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const suggerimentiRef = useRef<HTMLDivElement>(null);

  const [form, setForm] = useState<FormState>({
    conto_id: '',
    iban_destinatario: '',
    nome_destinatario: '',
    importo: '',
    causale: '',
    fornitore_id: '',
  });

  // Carica conti correnti
  useEffect(() => {
    fetch('/api/v1/banca/conti', { headers: authGet() })
      .then((r) => (r.ok ? r.json() : []))
      .then((data: Conto[]) => setConti(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // Carica soggetti fornitori per autocomplete
  useEffect(() => {
    fetch('/api/v1/soggetti?tipo=fornitore&limit=300', { headers: authGet() })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        const items: Soggetto[] = Array.isArray(data) ? data : (data.items ?? []);
        setSoggetti(items);
      })
      .catch(() => {});
  }, []);

  // Chiudi suggerimenti se click fuori
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (suggerimentiRef.current && !suggerimentiRef.current.contains(e.target as Node)) {
        setShowSuggerimenti(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  /** Aggiorna un campo del form. */
  const setField = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  /** Filtra i soggetti in base al testo digitato nel campo destinatario. */
  const handleNomeChange = useCallback(
    (value: string) => {
      setField('nome_destinatario', value);
      setField('fornitore_id', '');
      if (value.length >= 2) {
        const q = value.toLowerCase();
        const matches = soggetti
          .filter((s) => nomeDisplay(s).toLowerCase().includes(q))
          .slice(0, 8);
        setFiltroSoggetti(matches);
        setShowSuggerimenti(matches.length > 0);
      } else {
        setShowSuggerimenti(false);
      }
    },
    [soggetti, setField],
  );

  /** Seleziona un soggetto dall'autocomplete e compila i campi correlati. */
  const selezionaSoggetto = useCallback(
    (s: Soggetto) => {
      setForm((prev) => ({
        ...prev,
        nome_destinatario: nomeDisplay(s),
        iban_destinatario: s.iban ?? prev.iban_destinatario,
        fornitore_id: s.id,
      }));
      setShowSuggerimenti(false);
    },
    [],
  );

  /** Valida il form e invia la richiesta di bonifico. */
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setErrore(null);

      if (!form.conto_id) {
        setErrore('Seleziona il conto sorgente.');
        return;
      }
      if (!isIbanValido(form.iban_destinatario)) {
        setErrore('IBAN non valido. Deve iniziare con IT e avere 27 caratteri.');
        return;
      }
      if (!form.nome_destinatario.trim()) {
        setErrore('Il nome del destinatario è obbligatorio.');
        return;
      }
      const importoNum = parseFloat(form.importo);
      if (isNaN(importoNum) || importoNum <= 0) {
        setErrore("L'importo deve essere un numero positivo.");
        return;
      }
      if (!form.causale.trim()) {
        setErrore('La causale è obbligatoria.');
        return;
      }

      setSaving(true);
      try {
        const body: Record<string, unknown> = {
          conto_id: form.conto_id,
          iban_destinatario: form.iban_destinatario.replace(/\s/g, '').toUpperCase(),
          nome_destinatario: form.nome_destinatario.trim(),
          importo: importoNum.toFixed(2),
          causale: form.causale.trim(),
        };
        if (form.fornitore_id) body.fornitore_id = form.fornitore_id;

        const res = await fetch('/api/v1/banca/bonifici', {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          setErrore(err?.detail ?? 'Errore durante la creazione del bonifico.');
          return;
        }

        const data = await res.json();
        if (data.url_autorizzazione) {
          window.location.href = data.url_autorizzazione;
        } else {
          navigate('/banca/bonifici');
        }
      } catch {
        setErrore('Errore di rete. Riprova.');
      } finally {
        setSaving(false);
      }
    },
    [form, navigate],
  );

  return (
    <div className="p-6 max-w-2xl space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Bonifici
        </Button>
        <h1 className="text-xl font-outfit font-semibold text-foreground">Nuovo bonifico</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Dati del bonifico</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Conto sorgente */}
            <div className="space-y-1.5">
              <Label htmlFor="conto_id">Conto sorgente *</Label>
              <Select
                value={form.conto_id}
                onValueChange={(v) => setField('conto_id', v)}
              >
                <SelectTrigger id="conto_id">
                  <SelectValue placeholder="Seleziona conto…" />
                </SelectTrigger>
                <SelectContent>
                  {conti.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.nome} — {c.iban} ({c.valuta})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Nome destinatario con autocomplete */}
            <div className="space-y-1.5 relative" ref={suggerimentiRef}>
              <Label htmlFor="nome_destinatario">Nome destinatario *</Label>
              <Input
                id="nome_destinatario"
                value={form.nome_destinatario}
                onChange={(e) => handleNomeChange(e.target.value)}
                placeholder="Es. Mario Rossi S.r.l."
                autoComplete="off"
              />
              {showSuggerimenti && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-card border border-border rounded shadow-lg max-h-48 overflow-y-auto">
                  {filtroSoggetti.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors"
                      onClick={() => selezionaSoggetto(s)}
                    >
                      <span className="font-medium">{nomeDisplay(s)}</span>
                      {s.iban && (
                        <span className="ml-2 text-xs text-muted-foreground">{s.iban}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* IBAN destinatario */}
            <div className="space-y-1.5">
              <Label htmlFor="iban_destinatario">IBAN destinatario *</Label>
              <Input
                id="iban_destinatario"
                value={form.iban_destinatario}
                onChange={(e) =>
                  setField('iban_destinatario', e.target.value.toUpperCase())
                }
                placeholder="IT60 X054 2811 1010 0000 0123 456"
                maxLength={34}
              />
              {form.iban_destinatario.length > 3 && !isIbanValido(form.iban_destinatario) && (
                <p className="text-xs text-destructive">
                  IBAN non valido — deve iniziare con IT e avere 27 caratteri.
                </p>
              )}
            </div>

            {/* Importo */}
            <div className="space-y-1.5">
              <Label htmlFor="importo">Importo (€) *</Label>
              <Input
                id="importo"
                type="number"
                step="0.01"
                min="0.01"
                value={form.importo}
                onChange={(e) => setField('importo', e.target.value)}
                placeholder="0,00"
              />
            </div>

            {/* Causale */}
            <div className="space-y-1.5">
              <Label htmlFor="causale">Causale *</Label>
              <textarea
                id="causale"
                value={form.causale}
                onChange={(e) => setField('causale', e.target.value)}
                placeholder="Es. Pagamento fattura n. 123/2026"
                rows={2}
                maxLength={140}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              />
            </div>

            {/* Fornitore collegato (read-only, auto-fill) */}
            {form.fornitore_id && (
              <div className="space-y-1.5">
                <Label>Fornitore collegato</Label>
                <div className="text-sm text-muted-foreground bg-muted/40 px-3 py-2 rounded border border-border">
                  {form.nome_destinatario}{' '}
                  <span className="text-xs opacity-60">(selezionato da anagrafica)</span>
                </div>
              </div>
            )}

            {/* Errore */}
            {errore && (
              <div className="rounded bg-destructive/10 border border-destructive/20 px-3 py-2 text-sm text-destructive">
                {errore}
              </div>
            )}

            {/* Azioni */}
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={saving}>
                <Send className="h-4 w-4 mr-2" />
                {saving ? 'Invio in corso…' : 'Disponi bonifico'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                Annulla
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default NuovoBonifico;
